/**
 * SSE client for POST /api/chat/send.
 *
 * The vendored `configureApi` wrapper parses JSON responses, so it can't be used
 * for the text/event-stream chat endpoint. This dedicated streamer still routes
 * its bearer token through the SAME resolver seam (acquireToken) so auth stays
 * single-sourced — it does not bypass it. At merge the token source swaps with
 * the rest of the app (Entra → Auth0) by changing only src/auth/.
 *
 * Frame protocol (from apps/experts/api/chat/send.js):
 *   data: {"content":"..."}                       repeated assistant text deltas
 *   data: {"rag_chunks":[...]}                     retrieved KB context (eval-gated)
 *   data: {"citations":[...],"has_unsourced_claim":bool}  inline source citations
 *                                                 (final metadata frame, before [DONE])
 *   data: {"bench_trace":{...}}                    debug trace
 *   data: {"error":"..."}                          upstream error
 *   data: [DONE]                                   terminal sentinel
 * Comment lines (": heartbeat", ": cascade-buffering") are ignored.
 *
 * The pure frame protocol (decodeFrame + readSseStream) is exported so the
 * embeddable chat widget (src/widget/) shares ONE decoder with the SPA — there
 * is no second, divergent SSE parser. Only the auth source differs (the SPA uses
 * the resolver seam's acquireToken; the widget uses its own popup MSAL bearer).
 */
import { acquireToken } from '@/auth/msal';
import { AuthTokenUnavailableError } from '@andavo/web-ui/api';
import type {
  ChatSendRequest,
  ChatStreamEvent,
  Citation,
} from '@/features/chat/api-types';
import { chatUrl, getChatScopes } from '@/features/chat/chat-config';

const SEND_PATH = '/api/chat/send';

/**
 * Per-request transport config for the chat endpoints (they bypass the
 * configureApi wrapper). `scopes` is the CHAT-audience scope passed to the
 * scope-parameterized acquireToken; `baseUrl` is injected (same-origin by
 * default). Both default to the chat-config resolver — NOT module-level
 * constants: they flow from config so the token carries the correct audience
 * under whatever Entra topology lands. Tests may override.
 */
export type ChatRequestConfig = {
  scopes?: string[];
  baseUrl?: string;
};

/**
 * Decode a single SSE `data:` payload into a typed event, or null if it is not
 * a recognised frame. Pure — shared by the SPA streamer and the widget.
 */
export function decodeFrame(data: string): ChatStreamEvent | null {
  if (data === '[DONE]') return { kind: 'done' };
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(data) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof parsed.content === 'string') {
    return { kind: 'content', content: parsed.content };
  }
  if (Array.isArray(parsed.rag_chunks)) {
    return { kind: 'rag_chunks', chunks: parsed.rag_chunks };
  }
  // Inline source citations — the final metadata frame, emitted before [DONE].
  // Carries the structured citation array + the no-authoritative-source flag.
  if (Array.isArray(parsed.citations)) {
    return {
      kind: 'citations',
      citations: parsed.citations as Citation[],
      hasUnsourcedClaim: parsed.has_unsourced_claim === true,
    };
  }
  if ('bench_trace' in parsed) {
    return { kind: 'bench_trace', trace: parsed.bench_trace };
  }
  if (typeof parsed.error === 'string') {
    return { kind: 'error', message: parsed.error };
  }
  return null;
}

/**
 * Read a `text/event-stream` Response body, decode each `data:` frame, and
 * invoke `onEvent` until the `[DONE]` sentinel or the stream closes. Pure over
 * the Response — shared by the SPA streamer and the widget so the frame buffering
 * (blank-line-separated frames, comment/heartbeat skipping) lives in ONE place.
 */
export async function readSseStream(
  res: Response,
  onEvent: (event: ChatStreamEvent) => void,
): Promise<void> {
  if (!res.ok || !res.body) {
    throw new Error(`/api/chat/send failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      for (const line of frame.split('\n')) {
        const trimmed = line.trimStart();
        if (!trimmed.startsWith('data:')) continue; // skip comments/heartbeats
        const data = trimmed.slice('data:'.length).trim();
        if (!data) continue;
        const event = decodeFrame(data);
        if (event) {
          onEvent(event);
          if (event.kind === 'done') return;
        }
      }
    }
  }
}

/**
 * Stream a chat completion. Invokes `onEvent` for each decoded frame until the
 * `[DONE]` sentinel or the stream closes. Honour the AbortSignal to cancel.
 *
 * Mints a CHAT-audience token via the scope-parameterized acquireToken(scopes)
 * (auth_unification) and fetches against the injected baseUrl — both from the
 * chat-config resolver, overridable via `config` for tests.
 */
export async function streamChat(
  body: ChatSendRequest,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
  config: ChatRequestConfig = {},
): Promise<void> {
  const scopes = config.scopes ?? getChatScopes();
  const token = await acquireToken(scopes);
  if (!token) {
    // Mirror configureApi: never send an unauthenticated request.
    throw new AuthTokenUnavailableError();
  }

  const url = config.baseUrl
    ? `${config.baseUrl.replace(/\/$/, '')}/${SEND_PATH.replace(/^\//, '')}`
    : chatUrl(SEND_PATH);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  await readSseStream(res, onEvent);
}
